import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { getCookie } from "../../tools/tools";
import BootstrapHistogramPlotly from "./BootstrapHistogramPlotly";

const DEFAULT_TAXA = [
  "Actinomorphic Circularis",
  "Isobilateral Late elliptica Araphid",
  "Isobilateral Late elliptica Raphid",
  "Isobilateral Elliptica Araphid",
  "Isobilateral Elliptica Raphid",
  "Isobilateral Anguste elliptica Araphid",
  "Isobilateral Anguste elliptica Raphid",
  "Isobilateral Late lanceolata Araphid",
  "Isobilateral Late lanceolata Raphid",
  "Isobilateral Lanceolata Araphid",
  "Isobilateral Lanceolata Raphid",
  "Isobilateral Anguste lanceolata Araphid",
  "Isobilateral Anguste lanceolata Raphid",
  "Isobilateral Acicularis",
  "Isobilateral Rectangularis",
  "Isobilateral Oblonga",
  "Isobilateral Linearis",
  "Isobilateral Fusiform",
  "Isobilateral Pandurata",
  "Isobilateral Rhombica Raphid",
  "Isopolar dorsiventral Araphid",
  "Isopolar dorsiventral Raphid",
  "Heteropolar bilateral",
  "Asymmetrical",
];

const initRows = () =>
  DEFAULT_TAXA.map((t) => ({ name: t, a: 0, b: 0, hidden: false }));

const numberOrZero = (val) => {
  const n = parseFloat(val);
  return isNaN(n) ? 0 : n;
};

const PlanktonCompareV1 = () => {
  const [rows, setRows] = useState(initRows);
  const [dominantMode, setDominantMode] = useState("top_k");
  const [topK, setTopK] = useState(3);
  const [domThr, setDomThr] = useState(0.05);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState([]);
  const [result, setResult] = useState(null);
  const [newTaxon, setNewTaxon] = useState("");

  // Debug demo data flag via query param ?demo=1
  const DEMO_A = [
    9966, 28, 4, 206, 171, 56, 52, 7, 7, 132, 51, 376, 28, 119, 93, 87, 80, 26, 61, 5, 64, 26, 37, 40,
  ];
  const DEMO_B = [
    10229, 24, 3, 190, 152, 63, 48, 7, 6, 128, 54, 390, 23, 96, 85, 90, 65, 22, 63, 4, 70, 28, 42, 45,
  ];
  const DEMO_FLAG = useMemo(() => new URLSearchParams(window.location.search).get("demo") === "1", []);

  const applyDemoData = () => {
    setRows((rs) =>
      rs.map((r, i) => ({ ...r, a: DEMO_A[i] ?? 0, b: DEMO_B[i] ?? 0, hidden: false }))
    );
  };

  const clearData = () => {
    setRows((rs) => rs.map((r) => ({ ...r, a: 0, b: 0 })));
    resetAll();
  };

  useEffect(() => {
    if (DEMO_FLAG) {
      applyDemoData();
      setDominantMode("threshold");
      setDomThr(0.01);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [DEMO_FLAG]);

  const visibleRows = useMemo(() => rows.filter((r) => !r.hidden), [rows]);

  const updateRow = (idx, patch) => {
    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const toggleHideZeros = () => {
    const next = rows.map((r) => ({ ...r, hidden: r.a === 0 && r.b === 0 ? true : r.hidden }));
    setRows(next);
  };

  const showAll = () => setRows((rs) => rs.map((r) => ({ ...r, hidden: false })));

  const addTaxon = () => {
    const name = newTaxon.trim();
    if (!name) return;
    if (rows.some((r) => r.name.toLowerCase() === name.toLowerCase())) return;
    setRows((rs) => [...rs, { name, a: 0, b: 0, hidden: false }]);
    setNewTaxon("");
  };

  // Keep "All taxa details" in the same order as rows in the form
  const allItemsOrdered = useMemo(() => {
    if (!result || !result.indval || !Array.isArray(result.indval.items)) return [];
    const byTaxon = new Map(result.indval.items.map((it) => [it.taxon, it]));
    return rows
      .map((r) => byTaxon.get(r.name))
      .filter((x) => !!x);
  }, [result, rows]);

  const buildPayload = () => {
    const a = {};
    const b = {};
    rows.forEach((r) => {
      // Hidden rows are still included (user can delete if not needed)
      a[r.name] = numberOrZero(r.a);
      b[r.name] = numberOrZero(r.b);
    });
    return {
      sample_a: a,
      sample_b: b,
      dominant_mode: dominantMode,
      dominant_top_k: parseInt(topK) || 3,
      dominant_threshold: numberOrZero(domThr),
    };
  };

  const handleSubmit = async () => {
    setBusy(true);
    setErrors([]);
    setResult(null);
    try {
      const payload = buildPayload();
      const response = await axios.post("/api/plankton-compare/v1", payload, {
        headers: {
          "X-CSRFToken": getCookie("csrftoken"),
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });
      setResult(response.data);
    } catch (e) {
      const data = e?.response?.data || {};
      const errs = data?.errors || [e.message || "Request failed"];
      setErrors(Array.isArray(errs) ? errs : [JSON.stringify(errs)]);
    } finally {
      setBusy(false);
    }
  };

  const [fileAName, setFileAName] = useState("");
  const [fileBName, setFileBName] = useState("");
  const [fileAKey, setFileAKey] = useState(0);
  const [fileBKey, setFileBKey] = useState(0);

  const resetAll = () => {
    // обнуляем значения A/B во всех строках
    setRows((rs) => rs.map((r) => ({ ...r, a: 0, b: 0 })));
    // чистим ошибки/результат
    setErrors([]);
    setResult(null);
    // чистим имена файлов
    setFileAName("");
    setFileBName("");
    // форс-очистка полей выбора файлов
    setFileAKey((k) => k + 1);
    setFileBKey((k) => k + 1);
  };

  const resetSide = (side) => {
    // обнуляем только соответствующий столбец
    setRows((rs) => rs.map((r) => ({ ...r, [side]: 0 })));

    // пересчёт результата станет невалидным
    setResult(null);

    // очищаем имя файла и сам file-input только для этой стороны
    if (side === "a") {
      setFileAName("");
      setFileAKey((k) => k + 1);
    } else {
      setFileBName("");
      setFileBKey((k) => k + 1);
    }
  };

  const readFileAsText = (file) =>
    new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = () => reject(new Error("Не удалось прочитать файл"));
      fr.readAsText(file);
    });

  const parseAnnotationsFromXml = (xmlText) => {
    const parser = new DOMParser();
    const xml = parser.parseFromString(xmlText, "application/xml");
    const parserError = xml.getElementsByTagName("parsererror")[0];
    if (parserError) {
      throw new Error("Невалидный XML");
    }

    // Считаем количество Region внутри каждого Annotation (по имени таксона)
    const annotations = Array.from(xml.getElementsByTagName("Annotation"));
    const counts = new Map();

    for (const ann of annotations) {
      const name = ann.getAttribute("Name")?.trim();
      if (!name) continue;

      // Regions могут быть вложены; считаем все <Region> в пределах аннотации
      const regionsContainers = ann.getElementsByTagName("Regions");
      let regionCount = 0;
      for (const rc of regionsContainers) {
        regionCount += rc.getElementsByTagName("Region").length;
      }

      counts.set(name, (counts.get(name) || 0) + regionCount);
    }

    return counts;
  };

  const applyCountsToRows = (counts, side /* 'a' | 'b' */) => {
    setRows((rs) => {
      const byName = new Map(rs.map((r, i) => [r.name.toLowerCase(), { idx: i, r }]));
      const next = [...rs];

      // Записываем значения для найденных в XML таксонов;
      // неизвестные таксоны добавляем новыми строками
      counts.forEach((val, name) => {
        const key = name.toLowerCase();
        if (byName.has(key)) {
          const { idx } = byName.get(key);
          next[idx] = { ...next[idx], [side]: val };
        } else {
          next.push({
            name,
            a: side === "a" ? val : 0,
            b: side === "b" ? val : 0,
            hidden: false,
          });
        }
      });

      return next;
    });
  };

  const handleXmlUpload = async (side, file) => {
    if (!file) return;
    resetSide(side);
    setBusy(true);
    setErrors([]);
    try {
      const text = await readFileAsText(file);
      const counts = parseAnnotationsFromXml(text);
      applyCountsToRows(counts, side);
      if (side === "a") setFileAName(file.name);
      if (side === "b") setFileBName(file.name);
    } catch (e) {
      setErrors((prev) => [...prev, `Ошибка разбора ${side.toUpperCase()} XML: ${e.message || e}`]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container pt-5">
      <div className="row">
        <div className="col">
          <h2>Qualitative Dual Sample Taxa Analyzer</h2>
          <p className="text-muted">Qualitative comparison for two samples. Add/hide taxa as needed.</p>
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-md-3">
          <div className="form-group">
            <label className="form-control-label">Dominant mode</label>
            <select className="custom-select" value={dominantMode} onChange={(e) => setDominantMode(e.target.value)}>
              <option value="top_k">Top-K</option>
              <option value="threshold">Threshold</option>
            </select>
          </div>
        </div>
        {dominantMode === "top_k" ? (
          <div className="col-md-3">
            <div className="form-group">
              <label className="form-control-label">Top-K</label>
              <input
                type="number"
                className="form-control form-control-alternative"
                min={1}
                value={topK}
                onChange={(e) => setTopK(e.target.value)}
              />
            </div>
          </div>
        ) : (
          <div className="col-md-3">
            <div className="form-group">
              <label className="form-control-label">Dominant threshold (0..1)</label>
              <input
                type="number"
                className="form-control form-control-alternative"
                min={0}
                max={1}
                step="any"
                value={domThr}
                onChange={(e) => setDomThr(e.target.value)}
              />
            </div>
          </div>
        )}
      </div>

      <div className="row mb-2">
        <div className="col">
          {DEMO_FLAG && (
            <button className="btn btn-outline-primary mr-2" onClick={applyDemoData}>
              Load demo data
            </button>
          )}
          <button className="btn btn-outline-secondary mr-2" onClick={toggleHideZeros}>
            Hide zero rows
          </button>
          <button className="btn btn-outline-secondary mr-2" onClick={showAll}>
            Show all
          </button>
          <button className="btn btn-outline-danger" onClick={clearData}>
            Clear
          </button>
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-md-6">
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="Add new taxon"
              value={newTaxon}
              onChange={(e) => setNewTaxon(e.target.value)}
            />
            <button className="btn btn-outline-primary" onClick={addTaxon}>
              Add
            </button>
          </div>
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-md-6 mb-2">
          <label className="form-control-label d-block">Загрузить XML для Sample A</label>
          <div className="input-group">
            <input
              key={fileAKey}  
              type="file"
              className="form-control"
              accept=".xml"
              onChange={(e) => handleXmlUpload("a", e.target.files?.[0] || null)}
              disabled={busy}
            />
            {fileAName && <span className="input-group-text">{fileAName}</span>}
          </div>
          <small className="text-muted">
            Значения будут выставлены как количество &lt;Region&gt; для каждого таксона.
          </small>
        </div>
        <div className="col-md-6 mb-2">
          <label className="form-control-label d-block">Загрузить XML для Sample B</label>
          <div className="input-group">
            <input
              key={fileBKey}  
              type="file"
              className="form-control"
              accept=".xml"
              onChange={(e) => handleXmlUpload("b", e.target.files?.[0] || null)}
              disabled={busy}
            />
            {fileBName && <span className="input-group-text">{fileBName}</span>}
          </div>
          <small className="text-muted">
            Неизвестные таксоны будут добавлены в список автоматически.
          </small>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-sm align-middle">
          <thead>
            <tr>
              <th style={{ width: "2rem" }}></th>
              <th>Taxon</th>
              <th className="text-end">Sample A</th>
              <th className="text-end">Sample B</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((r, idx) => (
              <tr key={r.name}>
                <td>
                  <input
                    type="checkbox"
                    checked={r.hidden}
                    onChange={(e) => updateRow(idx, { hidden: e.target.checked })}
                    title="Hide this row"
                  />
                </td>
                <td>{r.name}</td>
                <td>
                  <input
                    type="number"
                    step="any"
                    className="form-control form-control-sm text-end"
                    value={r.a}
                    onChange={(e) => updateRow(idx, { a: e.target.value })}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    step="any"
                    className="form-control form-control-sm text-end"
                    value={r.b}
                    onChange={(e) => updateRow(idx, { b: e.target.value })}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="row mb-3">
        <div className="col">
          <button className="btn btn-primary" onClick={handleSubmit} disabled={busy}>
            {busy ? 'Calculating...' : 'Calculate'}
          </button>
        </div>
        <div className="mt-2">
          <small className="text-muted">
            By clicking Calculate, you agree to the{' '}
            <Link to="/agreement">User Agreement</Link>.
          </small>
        </div>
      </div>

      {errors.length > 0 && (
        <div className="alert alert-danger">
          {errors.map((e, i) => (
            <div key={i}>{e}</div>
          ))}
        </div>
      )}

      {result && (
        <div className="row mt-4">
          <div className="col-lg-6 mb-4">
            <h4>Metrics</h4>
            <table className="table table-sm">
              <tbody>
                <tr>
                  <td>Bray-Curtis distance</td>
                  <td className="text-end">{result.metrics.bray_curtis_distance.toFixed(6)}</td>
                </tr>
                <tr>
                  <td>Hellinger distance</td>
                  <td className="text-end">{result.metrics.hellinger_distance.toFixed(6)}</td>
                </tr>
                <tr>
                  <td>Jaccard index</td>
                  <td className="text-end">{result.metrics.jaccard_index.toFixed(6)}</td>
                </tr>
                <tr>
                  <td>Sørensen-Dice</td>
                  <td className="text-end">{result.metrics.sorensen_dice.toFixed(6)}</td>
                </tr>
                <tr>
                  <td>Dominant overlap (%)</td>
                  <td className="text-end">{result.metrics.dominant_overlap_percent.toFixed(2)}</td>
                </tr>
                <tr>
                  <td>Spearman rho</td>
                  <td className="text-end">{result.metrics.spearman_rho === null ? "-" : result.metrics.spearman_rho.toFixed(6)}</td>
                </tr>
                <tr>
                  <td>Kendall tau</td>
                  <td className="text-end">{result.metrics.kendall_tau === null ? "-" : result.metrics.kendall_tau.toFixed(6)}</td>
                </tr>
                <tr>
                  <td>Simpson dominance (A)</td>
                  <td className="text-end">{result.metrics.simpson_dominance.sample_a.toFixed(6)}</td>
                </tr>
                <tr>
                  <td>Simpson dominance (B)</td>
                  <td className="text-end">{result.metrics.simpson_dominance.sample_b.toFixed(6)}</td>
                </tr>
                <tr>
                  <td>Rel. dominance difference</td>
                  <td className="text-end">{result.metrics.relative_dominance_difference.toFixed(6)}</td>
                </tr>
                {result.indval && typeof result.indval.community_stability_index === "number" && (
                <tr>
                  <td>Community stability index (IS)</td>
                  <td className="text-end">
                    {result.indval.community_stability_index.toFixed(6)}
                  </td>
                </tr>
              )}
              </tbody>
            </table>

            {Array.isArray(result.metrics.bray_curtis_null_samples) && result.metrics.bray_curtis_null_samples.length > 0 && (
              <div className="mt-3">
                <h5 className="h6">Bray-Curtis bootstrap null</h5>
                <BootstrapHistogramPlotly
                  nullSamples={result.metrics.bray_curtis_null_samples}
                  observed={result.metrics.bray_curtis_distance}
                  pValue={result.metrics.bray_curtis_p_value}
                />
              </div>
            )}

            {Array.isArray(result.metrics.hellinger_null_samples) && result.metrics.hellinger_null_samples.length > 0 && (
              <div className="mt-3">
                <h5 className="h6">Hellinger bootstrap null</h5>
                <BootstrapHistogramPlotly
                  nullSamples={result.metrics.hellinger_null_samples}
                  observed={result.metrics.hellinger_distance}
                  pValue={result.metrics.hellinger_p_value}
                />
              </div>
            )}
          </div>

          <div className="col-lg-6 mb-4">
            <h4>Top Indicators (Indi)</h4>
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>Taxon</th>
                  <th className="text-end">SPi</th>
                  <th className="text-end">SCi</th>
                  <th className="text-end">RDi</th>
                  <th className="text-end">Indi</th>
                  <th className="text-end">Indicator</th>
                </tr>
              </thead>
              <tbody>
                {result.indval.top10.map((it) => (
                  <tr key={it.taxon}>
                    <td>{it.taxon}</td>
                    <td className="text-end">{it.SPi.toFixed(3)}</td>
                    <td className="text-end">{it.SCi.toFixed(3)}</td>
                    <td className="text-end">{it.RDi.toFixed(3)}</td>
                    <td className="text-end fw-bold">{it.Indi.toFixed(3)}</td>
                    <td className="text-end">{it.indicator_sample || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="col-12">
            <details>
              <summary>All taxa details</summary>
              <div className="table-responsive mt-3">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Taxon</th>
                      <th className="text-end">p(A)</th>
                      <th className="text-end">p(B)</th>
                      <th className="text-end">SPi</th>
                      <th className="text-end">SCi</th>
                      <th className="text-end">RDi</th>
                      <th className="text-end">Indi</th>
                      <th className="text-end">Indicator</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allItemsOrdered.map((it) => (
                      <tr key={it.taxon}>
                        <td>{it.taxon}</td>
                        <td className="text-end">{it.p_a.toFixed(6)}</td>
                        <td className="text-end">{it.p_b.toFixed(6)}</td>
                        <td className="text-end">{it.SPi.toFixed(3)}</td>
                        <td className="text-end">{it.SCi.toFixed(3)}</td>
                        <td className="text-end">{it.RDi.toFixed(3)}</td>
                        <td className="text-end">{it.Indi.toFixed(3)}</td>
                        <td className="text-end">{it.indicator_sample || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanktonCompareV1;
